import { useState, useCallback } from 'react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { frontendToBackendFieldType } from '@/lib/utils/procedure-field-types';

/**
 * Structure des réponses pour un rapport de procédure
 * @typedef {Object} ProcedureAnswers
 * @property {Array<{id: string|number, fields: Array<{id: string|number, type: string, value: string|string[]|null}>}>} sections
 */

/**
 * Payload pour créer un rapport de procédure
 * @typedef {Object} CreateProcedureReportPayload
 * @property {string} name
 * @property {string} templateUniqId
 * @property {ProcedureAnswers} answers
 * @property {string} [parentSubfolderId]
 * @property {number} [latitude]
 * @property {number} [longitude]
 */

/**
 * Hook pour créer un rapport de procédure (instance)
 *
 * @returns {{
 *   createReport: (folderId: string, payload: CreateProcedureReportPayload) => Promise<any>,
 *   isCreating: boolean,
 *   error: Error | null,
 *   reset: () => void
 * }}
 *
 * @example
 * const { createReport, isCreating, error } = useCreateProcedureReport();
 *
 * const handleSubmit = async (formData) => {
 *     const answers = buildAnswersFromFormData(template.configuration, formData);
 *     const result = await createReport(folderId, {
 *         name: 'Mon rapport',
 *         templateUniqId: 'abc123def',
 *         answers,
 *     });
 *     if (result) {
 *         // URL simplifiée
 *         navigate(`/folders/${result.uniq_id}`);
 *     }
 * };
 */
export function useCreateProcedureReport() {
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Crée un rapport de procédure dans un dossier existant
     * @param {string} folderId - ID du dossier existant
     * @param {object} payload - Données du rapport
     */
    const createReportInExistingFolder = useCallback(async (folderId, payload) => {
        setIsCreating(true);
        setError(null);

        try {
            const response = await horodatyApi.createProcedureReportInExistingFolder(folderId, {
                name: payload.name,
                template_id: payload.templateId,
                answers: payload.answers,
                type: 'procedure',
            });

            setIsCreating(false);
            return response.data?.data || response.data;
        } catch (err) {
            setError(err);
            setIsCreating(false);
            throw err;
        }
    }, []);

    /**
     * Crée un nouveau dossier et un rapport de procédure dedans
     * @param {string} folderName - Nom du nouveau dossier
     * @param {object} payload - Données du rapport
     */
    const createReportWithNewFolder = useCallback(async (folderName, payload) => {
        setIsCreating(true);
        setError(null);

        try {
            const response = await horodatyApi.createProcedureReportWithNewFolder({
                folder: {
                    nom: folderName,
                },
                procedure: {
                    name: payload.name,
                    template_id: payload.templateId,
                    answers: payload.answers,
                    type: 'procedure',
                },
            });

            setIsCreating(false);
            return response.data?.data || response.data;
        } catch (err) {
            setError(err);
            setIsCreating(false);
            throw err;
        }
    }, []);

    /**
     * Fonction principale pour créer un rapport de procédure
     * @deprecated Utiliser createReportInExistingFolder ou createReportWithNewFolder
     */
    const createReport = useCallback(async (folderId, payload) => {
        setIsCreating(true);
        setError(null);

        try {
            const response = await horodatyApi.createProcedureIntervention(folderId, {
                name: payload.name,
                template_id: payload.templateUniqId,
                answers: payload.answers,
                parent_subfolder_id: payload.parentSubfolderId,
                latitude: payload.latitude,
                longitude: payload.longitude,
            });

            setIsCreating(false);
            return response.data?.data || response.data;
        } catch (err) {
            setError(err);
            setIsCreating(false);
            throw err;
        }
    }, []);

    const reset = useCallback(() => {
        setError(null);
        setIsCreating(false);
    }, []);

    return {
        createReport,
        createReportInExistingFolder,
        createReportWithNewFolder,
        isCreating,
        error,
        reset,
    };
}

/**
 * Construit l'objet `answers` à partir des données du formulaire et de la configuration du template
 *
 * @param {object} configuration - Configuration du template (avec sections et fields)
 * @param {object} formData - Données du formulaire { [fieldId]: value }
 * @returns {ProcedureAnswers} - Structure answers pour l'API
 *
 * @example
 * const configuration = template.configuration;
 * const formData = { type_travaux: 'Isolation', surface: '120' };
 * const answers = buildAnswersFromFormData(configuration, formData);
 */
export function buildAnswersFromFormData(configuration, formData) {
    if (!configuration?.sections) {
        return { sections: [] };
    }

    return {
        sections: configuration.sections.map((section) => ({
            id: section.id,
            fields: (section.fields || []).map((field) => ({
                id: field.id,
                type: frontendToBackendFieldType(field.type),
                value: formData[field.id] ?? null,
            })),
        })),
    };
}

/**
 * Valide que tous les champs obligatoires sont remplis
 *
 * @param {object} configuration - Configuration du template
 * @param {object} formData - Données du formulaire
 * @returns {{ isValid: boolean, missingFields: Array<{ sectionId: string, fieldId: string, label: string }> }}
 */
export function validateRequiredFields(configuration, formData) {
    const missingFields = [];

    if (!configuration?.sections) {
        return { isValid: true, missingFields };
    }

    for (const section of configuration.sections) {
        for (const field of section.fields || []) {
            if (field.required) {
                const value = formData[field.id];
                const isEmpty =
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (Array.isArray(value) && value.length === 0);

                if (isEmpty) {
                    missingFields.push({
                        sectionId: section.id,
                        sectionName: section.name,
                        fieldId: field.id,
                        label: field.label,
                    });
                }
            }
        }
    }

    return {
        isValid: missingFields.length === 0,
        missingFields,
    };
}

/**
 * Initialise les valeurs par défaut du formulaire depuis la configuration
 *
 * @param {object} configuration - Configuration du template
 * @returns {object} - Valeurs initiales { [fieldId]: defaultValue }
 */
export function initializeFormData(configuration) {
    const initialData = {};

    if (!configuration?.sections) {
        return initialData;
    }

    for (const section of configuration.sections) {
        for (const field of section.fields || []) {
            // Valeurs par défaut selon le type
            switch (field.type) {
                case 'checkbox':
                    initialData[field.id] = [];
                    break;
                case 'photo':
                case 'photo_certified':
                case 'video_certified':
                    initialData[field.id] = [];
                    break;
                default:
                    initialData[field.id] = '';
            }
        }
    }

    return initialData;
}
